/**
 * PatientSignupForm — Self-Service-Registrierung im Patient-Portal.
 * Extrahiert aus `pages/Login.tsx` (UX-Audit Phase 5).
 */

import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  patientSignupSchema,
  type PatientSignupInput,
} from '@/schemas/auth';

interface Props {
  onSubmit: (values: PatientSignupInput) => Promise<void>;
}

export function PatientSignupForm({ onSubmit }: Props) {
  const form = useForm<PatientSignupInput>({
    resolver: zodResolver(patientSignupSchema),
    defaultValues: {
      email: '',
      password: '',
      agbAccepted: false as unknown as true,
    },
  });

  return (
    <Form {...form}>
      {/* F-01-08: invite-only notice — accounts created without a therapist
          invitation have no org link and are functionally useless.            */}
      <div className="flex gap-2.5 rounded-xl border border-amber-300/50 bg-amber-50 px-3.5 py-3 text-xs text-amber-800 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-300 mb-4">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Patient:innen-Zugänge werden von Ihrer Therapeut:in per{' '}
          <strong>Einladungs-Link</strong> erstellt. Falls Sie noch keinen Link
          erhalten haben, wenden Sie sich bitte direkt an Ihre Praxis.
        </span>
      </div>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="text-xs text-muted-foreground">E-Mail</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="ihre@email.de"
                  className="bg-secondary border-border focus:ring-1 focus:ring-primary"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="text-xs text-muted-foreground">Passwort</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  className="bg-secondary border-border focus:ring-1 focus:ring-primary"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="agbAccepted"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-start gap-2.5 pt-1">
                <FormControl>
                  <Checkbox
                    id="agb-accept-patient"
                    checked={field.value === true}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    className="mt-0.5"
                  />
                </FormControl>
                <label
                  htmlFor="agb-accept-patient"
                  className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
                >
                  Ich akzeptiere die{' '}
                  <Link to="/agb" target="_blank" className="text-primary underline hover:text-primary/80">AGB</Link>
                  {' '}und habe die{' '}
                  <Link to="/datenschutz" target="_blank" className="text-primary underline hover:text-primary/80">Datenschutzerklärung</Link>
                  {' '}gelesen.
                </label>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl h-10"
        >
          {form.formState.isSubmitting && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          )}
          Registrieren
        </Button>
      </form>
    </Form>
  );
}
